import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Users, BedDouble, Maximize, ChevronDown } from "lucide-react";
import { visibleRoomImages } from "../lib/roomImages";
import api from "../lib/api";
import { useContent } from "../context/ContentContext";
import PageHero from "../components/PageHero";
import ExternalLinks from "../components/ExternalLinks";
import { FacilityIcon } from "../lib/facilityIcons";
import { cropStyle } from "../lib/cropStyle";

export default function Accommodation() {
  const { content } = useContent();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const ph = content?.page_heroes?.accommodation || {};
  const rawFac = content?.facilities?.length ? content.facilities : (content?.amenities || []).map((a) => ({ label: a, icon: "Check", section: "" }));
  const facilityGroups = [];
  rawFac.forEach((f) => { const s = f.section || "General"; let g = facilityGroups.find((x) => x.section === s); if (!g) { g = { section: s, items: [] }; facilityGroups.push(g); } g.items.push(f); });
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => {
    if (content?.collapsed_sections) setCollapsed(Object.fromEntries(content.collapsed_sections.map((s) => [s, true])));
  }, [content]);

  useEffect(() => { api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <PageHero
        eyebrow={ph.eyebrow || "Accommodation"}
        title={ph.title || "Suites shaped by the mountains"}
        subtitle={ph.subtitle || "Each space is a private retreat — refined interiors, spa bathrooms and uninterrupted highland views."}
        image={ph.image || content?.hero_images?.[0] || rooms[0]?.images?.[0]}
      />

      <section className="py-20 container-lux space-y-20">
        {rooms.map((room, idx) => (
          <motion.div
            key={room.id} data-testid={`room-detail-${room.id}`}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className={`grid lg:grid-cols-2 gap-10 items-center ${idx % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}
          >
            <div className="grid grid-cols-2 gap-3">
              <img src={visibleRoomImages(room)[0]} alt={room.name} className="col-span-2 h-72 w-full object-cover object-center rounded-2xl" style={cropStyle(room.image_positions?.[visibleRoomImages(room)[0]], room.image_zooms?.[visibleRoomImages(room)[0]])} />
              {visibleRoomImages(room).slice(1, 3).map((im) => (
                <img key={im} src={im} alt={room.name} className="h-40 w-full object-cover object-center rounded-xl" style={cropStyle(room.image_positions?.[im], room.image_zooms?.[im])} />
              ))}
            </div>
            <div>
              {room.featured && <span className="eyebrow">Signature</span>}
              <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-2">{room.name}</h2>
              <div className="flex flex-wrap gap-5 mt-4 text-sm text-stone-500">
                <span className="flex items-center gap-1.5"><Users size={16} className="text-rose" /> Up to {room.capacity}</span>
                <span className="flex items-center gap-1.5"><BedDouble size={16} className="text-rose" /> {room.beds}</span>
                <span className="flex items-center gap-1.5"><Maximize size={16} className="text-rose" /> {room.size}</span>
              </div>
              <p className="mt-5 text-stone-600 leading-relaxed">{room.description}</p>
              <div className="mt-6 grid grid-cols-2 gap-2">
                {room.amenities?.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-sm text-stone-600">
                    <Check size={15} className="text-gold" /> {a}
                  </div>
                ))}
              </div>
              <div className="mt-8 flex items-center gap-6">
                <div><span className="font-serif text-3xl text-rose">${room.price}</span><span className="text-stone-400 text-sm"> / night</span></div>
                <div className="flex gap-2">
                  <button data-testid={`view-room-${room.id}`} onClick={() => navigate(`/rooms/${room.slug}`)} className="btn-dark">View details</button>
                  <button data-testid={`book-room-${room.id}`} onClick={() => navigate(`/booking?room_id=${room.id}`)} className="btn-primary">Book this suite</button>
                </div>
              </div>
              <ExternalLinks className="mt-4" label="Also on" />
            </div>
          </motion.div>
        ))}
      </section>

      {/* Amenities + Policies */}
      <section className="py-20 bg-sand/60">
        <div className="container-lux grid md:grid-cols-2 gap-14">
          <div>
            <span className="eyebrow">Facilities</span>
            <h2 className="font-serif text-3xl text-forest mt-2 mb-6">Everything you need</h2>
            <div className="space-y-5">
              {facilityGroups.map((g) => {
                const open = collapsed[g.section] !== true;
                return (
                  <div key={g.section} data-testid={`facility-group-${g.section}`}>
                    <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [g.section]: open }))} data-testid={`facility-group-toggle-${g.section}`} className="w-full flex items-center justify-between text-xs uppercase tracking-widest text-gold mb-3">
                      <span>{g.section}</span>
                      <ChevronDown size={16} className={`md:hidden transition-transform ${open ? "" : "-rotate-90"}`} />
                    </button>
                    <div className={`${open ? "grid" : "hidden"} md:grid grid-cols-2 gap-3`}>
                      {g.items.map((f, i) => (
                        g.section === "Activities" ? (
                          <button key={`${f.label}-${i}`} onClick={() => navigate(f.experience_id ? `/experiences?exp=${f.experience_id}` : `/experiences?q=${encodeURIComponent(f.label)}`)} data-testid={`facility-link-${f.label}`} className="flex items-center gap-2 text-sm text-stone-700 hover:text-rose transition-colors text-left group">
                            <FacilityIcon name={f.icon} size={16} className="text-rose" /> <span className="group-hover:underline">{f.label}</span>
                            <span className="ml-auto text-[10px] uppercase tracking-wide bg-rose/10 text-rose px-2 py-0.5 rounded-full" data-testid={`facility-badge-${f.label}`}>Bookable</span>
                          </button>
                        ) : (
                          <div key={`${f.label}-${i}`} className="flex items-center gap-2 text-sm text-stone-700" data-testid={`facility-item-${i}`}>
                            <FacilityIcon name={f.icon} size={16} className="text-rose" /> {f.label}
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <span className="eyebrow">Good to know</span>
            <h2 className="font-serif text-3xl text-forest mt-2 mb-6">Policies</h2>
            <ul className="space-y-3">
              {(content?.policies || []).map((p, i) => {
                const t = typeof p === "string" ? p : p.text;
                const ic = typeof p === "string" ? "Check" : (p.icon || "Check");
                return (
                  <li key={(typeof p === "string" ? p : p.text) || i} className="flex gap-3 text-sm text-stone-700" data-testid={`policy-item-${i}`}>
                    <FacilityIcon name={ic} size={16} className="text-gold shrink-0 mt-0.5" /> <span>{t}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
